import { FocusCss } from '@tvg/fuse-utils';
import styled from 'styled-components';
import { Icon } from '../../fuse-icon-element/src/icon-element';
import Typography from '../../fuse-typography-element/src/typography';
import { styledDropdownConfig } from './config';

const { borderColor, borderRadius, borderWidth, componentName, helperTextColor } =
	styledDropdownConfig;

const StyledWrapper = styled.div`
	&.fuse-dropdown--disabled div {
		background-color: unset;
	}

	&.fuse-dropdown--disabled label,
	&.fuse-dropdown--disabled span,
	&.fuse-dropdown--disabled p,
	&.fuse-dropdown--disabled select {
		color: var(--color-global-grey400);
		border-color: var(--color-global-grey400);
	}

	&.fuse-dropdown--disabled select {
		color: var(--color-global-grey400);
	}

	&.fuse-dropdown--disabled button {
		color: var(--color-global-grey400);

		&:hover,
		&:focus,
		&:active {
			outline: none;
			cursor: initial;
		}
	}

	&.fuse-dropdown--disabled div,
	&.fuse-dropdown--disabled input {
		border-color: var(--color-global-grey400);
		background-color: unset;
	}

	&.fuse-dropdown--disabled .${componentName}__expandIcon {
		background-color: var(--color-global-grey400);
	}
`;

const StyledLabel = styled.label.attrs({
	className: `${componentName}__label`,
	'data-testid': `${componentName}__label`,
})`
	color: var(--color-global-black);
	display: inline-block;
	font-weight: var(--font-weight-paragraph);
	font-size: var(--font-size-1);
	line-height: var(--font-line-height-1);
	margin-bottom: var(--spacing-global-space3);

	${({ size }) =>
		size === 'small' &&
		`
			font-size: var(--font-size-m2);
			line-height: var(--font-line-height-m2);
			margin-bottom: var(--spacing-global-space2);
		`}
`;

const StyledDropdownWrapper = styled.div.attrs({
	className: `${componentName}__wrapper`,
	'data-testid': `${componentName}__wrapper`,
})`
	background: var(--color-global-white);
	color: var(--color-global-grey900);
	display: flex;
	width: 100%;
	overflow: hidden;
	align-items: center;

	${borderRadius};

	${({ state }) => `
		border: ${borderWidth} solid ${borderColor?.[state] || `var(--color-global-grey400)`};

		${borderRadius};

		&.${componentName}__wrapper--selected {
			border-color: ${borderColor?.[state] || `var(--color-global-grey700)`};
		}

		&:hover, &:focus, &:focus-within {
			border-color: ${borderColor?.[state] || `var(--color-brand-mid)`};
		}

		&.${componentName}__wrapper--outline {
			${FocusCss}
		}
	`}

	${({ size }) =>
		size === 'small' &&
		`
		height: 32px;
		overflow: visible;
	`}
`;

const StyledSelect = styled.select.attrs({
	className: `${componentName}__select`,
	'data-testid': `${componentName}__select`,
})`
	background-color: #fff;
	display: flex;
	overflow: hidden;
	padding-top: 11px;
	padding-bottom: 11px;
	padding-left: var(--spacing-global-space4);
	padding-right: ${(props) => (props.isInDefaultState ? '44px' : '69px')};
	position: relative;
	text-align: left;
	transition: all 0.2s;
	width: 100%;
	margin: 0;
	background-image: none;
	appearance: none;
	cursor: pointer;
	color: var(--color-global-grey900);
	align-items: center;
	font-size: var(--font-size-1);
	line-height: var(--font-line-height-1);
	border: 0;
	outline: 0;
	margin-right: ${(props) => (props.isInDefaultState ? '-40px' : '-64px')};
	text-overflow: ellipsis;
	white-space: nowrap;
	box-sizing: border-box;

	${borderRadius};

	&:disabled {
		cursor: auto;
	}

	${({ size }) =>
		size === 'small' &&
		`
			padding-top: 14px;
			padding-bottom: 14px;
			padding-left: var(--spacing-global-space3);
			font-size: var(--font-size-m2);
			line-height: var(--font-line-height-m2);
			background: transparent;
		`}
`;

const StyledIconWrapper = styled.span.attrs({
	className: `${componentName}__iconWrapper`,
	'data-testid': `${componentName}__iconWrapper`,
})`
	display: flex;
	pointer-events: none;
	align-items: center;
`;

const StyledIcon = styled(Icon).attrs({
	className: `${componentName}__icon`,
	'data-testid': `${componentName}__icon`,
})`
	margin: 0;
`;

const StyledExpandIcon = styled(Icon).attrs({
	className: `${componentName}__expandIcon`,
	'data-testid': `${componentName}__expandIcon`,
})`
	margin: 0 var(--spacing-global-space3);
`;

const StyledDescriptionContainer = styled.div.attrs({
	className: `${componentName}__description`,
	'data-testid': `${componentName}__description`,
})`
	display: flex;
	justify-content: flex-start;
	padding-top: var(--spacing-global-space3);
`;

const StyledHelperText = styled(Typography).attrs({
	className: `${componentName}__helperText`,
	'data-testid': `${componentName}__helperText`,
})`
	color: ${({ state }) => helperTextColor?.[state] || `var(--color-global-grey800)`};
	flex: 1 1 auto;
`;

export {
	StyledSelect,
	StyledDropdownWrapper,
	StyledIconWrapper,
	StyledIcon,
	StyledExpandIcon,
	StyledLabel,
	StyledWrapper,
	StyledDescriptionContainer,
	StyledHelperText,
};
